/**
 * CR311: Deal Context Strip
 * 
 * Compact metadata row showing:
 * Merchant • Source • Deal ID • Visibility
 * 
 * Additional flags accessible via Details popover.
 */

import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import {
  Store,
  FileText,
  Hash,
  Eye,
  Info,
  Calendar,
  DollarSign,
  Percent,
  Link,
  Copy,
  Check,
} from "lucide-react";
import { useState, useCallback } from "react";
import type { SampleDeal } from "@/hooks/useDealCardTemplateEditor";

interface DealContextStripProps {
  sample: SampleDeal | null;
  className?: string;
}

export function DealContextStrip({ sample, className }: DealContextStripProps) {
  const [copied, setCopied] = useState(false);
  
  // Extract additional details from rawData
  const details = useMemo(() => {
    if (!sample) return { claimUrl: null, confidence: null, region: null, flyerPage: null };
    const raw = sample.rawData || {};
    return {
      claimUrl: raw.claim_url as string || raw.deal_link as string || null,
      confidence: raw.confidence as number | null,
      region: raw.region as string || null,
      flyerPage: raw.flyer_page_number as number | null,
    };
  }, [sample]);
  
  const handleCopyId = useCallback(() => {
    if (!sample) return;
    navigator.clipboard.writeText(sample.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [sample]);
  
  if (!sample) {
    return (
      <div className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}>
        <span>No deal selected</span>
      </div>
    );
  }
  
  return (
    <div className={cn("flex items-center gap-2 text-xs", className)}>
      {/* Main context line */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Store className="h-3 w-3" />
        <span className="font-medium text-foreground">{sample.merchant}</span>
      </div>
      
      <span className="text-muted-foreground/50">•</span>
      
      <div className="flex items-center gap-1">
        <FileText className="h-3 w-3 text-muted-foreground" />
        <span className="text-muted-foreground">{sample.sourceType || "—"}</span>
      </div>
      
      <span className="text-muted-foreground/50">•</span>
      
      <div className="flex items-center gap-1">
        <Hash className="h-3 w-3 text-muted-foreground" />
        <code className="text-[10px] text-muted-foreground font-mono">
          {sample.id.slice(0, 8)}...
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0"
          onClick={handleCopyId}
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3 text-muted-foreground" />
          )}
        </Button>
      </div>
      
      <span className="text-muted-foreground/50">•</span>
      
      <Badge 
        variant="secondary" 
        className={cn(
          "text-[9px] h-5",
          sample.dealType === "BONUS" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
          sample.dealType === "POINTS" && "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
          sample.dealType === "DISCOUNT" && "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
        )}
      >
        {sample.dealType}
      </Badge>
      
      {/* Details popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-1">
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end">
          <div className="space-y-3">
            <p className="text-xs font-medium">Deal Details</p>
            
            <div className="space-y-2 text-xs">
              {/* Value fields */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <DollarSign className="h-3 w-3" />
                  Computed Value
                </div>
                <span className="font-mono">
                  {sample.computedValueCad != null ? `$${sample.computedValueCad}` : "—"}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Percent className="h-3 w-3" />
                  Effective %
                </div>
                <span className="font-mono">
                  {sample.effectivePercentBack != null ? `${sample.effectivePercentBack}%` : "—"}
                </span>
              </div>
              
              <Separator />
              
              {/* Date fields */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Valid From
                </div>
                <span className="font-mono text-[10px]">
                  {sample.validFrom ? new Date(sample.validFrom).toLocaleDateString() : "—"}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Valid Until
                </div>
                <span className="font-mono text-[10px]">
                  {sample.validUntil ? new Date(sample.validUntil).toLocaleDateString() : "—"}
                </span>
              </div>
              
              <Separator />
              
              {/* Additional metadata */}
              {details.region && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Region</span>
                  <span>{details.region}</span>
                </div>
              )}
              
              {details.confidence != null && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Confidence</span>
                  <span>{Math.round(details.confidence * 100)}%</span>
                </div>
              )}
              
              {details.flyerPage && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Flyer Page</span>
                  <span>#{details.flyerPage}</span>
                </div>
              )}
              
              {details.claimUrl && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" className="w-full h-7 text-xs gap-1" asChild>
                    <a href={details.claimUrl} target="_blank" rel="noopener noreferrer">
                      <Link className="h-3 w-3" />
                      View Deal Source
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}